import { View } from "@common/types/views/views";

/** What a column settings row needs from a column's config. */
export interface ColumnSettingsConfig {
    name: string;
    internal?: boolean;
    immutable?: boolean;
    required?: boolean;
    hiddenFromSettings?: boolean;
    /** A note under the row's name, as a SettingsPanel row's hint. */
    hint?: string;
}

export interface ColumnData {
    id: string;
    name: string;
    options?: { pin?: "left" | "right" };
    internal: boolean;
    immutable?: boolean;
    required?: boolean;
    hint?: string;
}

/** A column that can be pinned to either end, as List shows it. */
interface PinnableColumn {
    options?: { pin?: "left" | "right" } | null;
}

/** Columns split the way List shows them: left pinned, unpinned, right pinned. */
export interface ColumnsByPin<T extends PinnableColumn> {
    leftPinnedColumns: T[];
    centerColumns: T[];
    rightPinnedColumns: T[];
}

export function separateColumnsByPin<T extends PinnableColumn>(
    columns: readonly T[],
): ColumnsByPin<T> {
    return {
        leftPinnedColumns: columns.filter((col) => col.options?.pin === "left"),
        centerColumns: columns.filter((col) => !col.options?.pin),
        rightPinnedColumns: columns.filter(
            (col) => col.options?.pin === "right",
        ),
    };
}

export function determineSectionFromDropId(
    overColId: string,
    columnSections: ColumnsByPin<ColumnData>,
) {
    const { leftPinnedColumns, centerColumns, rightPinnedColumns } =
        columnSections;

    if (leftPinnedColumns.some((col) => col.id === overColId)) {
        return { targetPin: "left" as const };
    }

    if (rightPinnedColumns.some((col) => col.id === overColId)) {
        return { targetPin: "right" as const };
    }

    if (centerColumns.some((col) => col.id === overColId)) {
        return { targetPin: undefined };
    }

    return null;
}

export function calculateInsertionIndex(
    overColId: string,
    activeColumnIndex: number,
    newColumns: View["columns"],
): number {
    return newColumns.findIndex((col) => col.id === overColId);
}

/** `next` with each column's pin put back as it was in `previous`, so a drag only reorders. */
export const withPinsKept = ({
    previous,
    next,
}: {
    previous: View["columns"];
    next: View["columns"];
}): View["columns"] => {
    const pinsById = new Map(
        previous.map((column) => [column.id, column.options?.pin]),
    );
    return next.map((column) => {
        const pin = pinsById.get(column.id);
        if (column.options?.pin === pin) {
            return column;
        }
        const { pin: _pin, ...otherOptions } = column.options ?? {};
        return {
            ...column,
            options: pin ? { ...otherOptions, pin } : otherOptions,
        };
    });
};
